import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { LineChart, BarChart } from '../../components/charts';
import { 
  Loader2, 
  BarChart3, 
  Calendar,
  Globe,
  Users,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  Filter,
  Download,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Search,
  SlidersHorizontal,
  ExternalLink,
  MousePointerClick,
  Clock,
  Zap,
  Info,
  ChevronDown,
  ChevronRight,
  Target
} from 'lucide-react';
import '../../context/AuthContext.css';

export default function SourceMedium() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last30');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'sessions', direction: 'desc' });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [trafficSourceData, setTrafficSourceData] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [expandedSources, setExpandedSources] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'monthly'
  const [sourceAnalytics, setSourceAnalytics] = useState({
    summary: {
      total_sessions: 0,
      total_users: 0,
      conversion_rate: 0,
      avg_session_duration: '0:00',
      bounce_rate: 0,
      revenue: 0
    },
    sources: []
  });

  // Helper function to format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Helper function to convert date range to API parameter
  const getApiDateRangeParam = (range) => {
    switch(range) {
      case 'today': return 'today';
      case 'yesterday': return 'yesterday';
      case 'last7': return 'last7';
      case 'last30': return 'last30';
      case 'last90': return 'last90';
      case 'thisMonth': return 'thisMonth';
      case 'lastMonth': return 'lastMonth';
      case 'all': return 'all';
      default: return 'last30';
    }
  };
  
  // Helper function to get date range for today, yesterday, this month, last month
  const getDateRangeForFilter = (filter) => {
    const today = new Date();
    let startDate, endDate;
    
    switch(filter) {
      case 'today':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 23, 59, 59);
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'lastMonth':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        break;
      case 'last7':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        endDate = today;
        break;
      case 'last30':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
        break;
      case 'last90':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90);
        endDate = today;
        break;
      case 'all':
        // For "all time", use a very early date
        startDate = new Date(2020, 0, 1); // January 1, 2020
        endDate = today;
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
    }
    
    return { startDate, endDate };
  };
  
  // Helper function to filter data by date range
  const filterDataByDateRange = (data, filter) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const { startDate, endDate } = getDateRangeForFilter(filter);
    
    return data.filter(item => {
      if (!item.date) return false;
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };
  
  // Fetch GA traffic source data
  const fetchGATrafficSourceData = async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage (currentBrandId)');
        return;
      }
      
      console.log('Fetching GA traffic source data for brand:', brandId);
      console.log('Using date range:', dateRange, 'and view mode:', viewMode);
      
      // Convert our date range to API parameter
      const apiDateRange = getApiDateRangeParam(dateRange);
      
      // Use different endpoint for monthly view
      const endpoint = viewMode === 'monthly' 
        ? `https://api.glowytics.com/api/google-ads/ga-traffic-monthly-summary?dateRange=${apiDateRange}`
        : `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=${apiDateRange}`;
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Save raw data to state
      setTrafficSourceData(data);
      
      // Process data for source/medium analysis based on view mode
      if (viewMode === 'monthly') {
        processMonthlyTrafficSourceData(data);
      } else {
        processTrafficSourceData(data);
      }
      
    } catch (error) {
      console.error('Error fetching GA traffic source data:', error.message);
      console.log('Error details:', error);
      setLoading(false);
    }
  };
  
  // Process monthly traffic source data for the Source/Medium analysis
  const processMonthlyTrafficSourceData = (data) => {
    if (!data || !data.monthlyData || !Array.isArray(data.monthlyData) || data.monthlyData.length === 0) {
      console.log('No monthly GA traffic source data available or empty data');
      setLoading(false);
      return;
    }
    
    console.log('Processing monthly traffic source data:', data.monthlyData);
    
    // For monthly view, we'll group by source/medium as well but with monthly aggregation
    const groups = [
      { 
        key: 'direct_none',
        displayName: 'Direct / none',
        match: (source, medium) => 
          source.includes('direct') && medium.includes('none')
      },
      { 
        key: 'google_organic',
        displayName: 'Google / Organic', 
        match: (source, medium) => 
          source.includes('google') && medium.includes('organic')
      },
      { 
        key: 'google_cpc',
        displayName: 'Google CPC', 
        match: (source, medium) => 
          source.includes('google') && medium.includes('cpc')
      },
      { 
        key: 'meta_cpc',
        displayName: 'Meta / CPC', 
        match: (source, medium) => 
          medium.includes('social-cpc') || 
          medium.includes('social_cpc') || 
          medium.includes('social')
      },
      { 
        key: 'rtb_house',
        displayName: 'RTB House', 
        match: (source, medium) => 
          source.includes('rtb')
      },
      { 
        key: 'mail',
        displayName: 'Mail', 
        match: (source, medium) => 
          source.includes('mail') || 
          medium.includes('mail') || 
          medium.includes('email')
      },
      { 
        key: 'push',
        displayName: 'Push', 
        match: (source, medium) => 
          source.includes('push') || 
          medium.includes('push')
      },
      { 
        key: 'other',
        displayName: 'Other', 
        match: () => true
      }
    ];
    
    // Initialize categories
    const categoryTotals = {};
    groups.forEach(group => {
      categoryTotals[group.key] = {
        source: group.displayName,
        sessions: 0,
        users: 0,
        new_users: 0,
        conversions: 0,
        revenue: 0,
        monthlyData: [],
        campaigns: {}
      };
    });
    
    let totalSessions = 0;
    let totalUsers = 0;
    let totalRevenue = 0;
    let totalConversions = 0;
    
    // Process monthly data - simpler structure for monthly view
    data.monthlyData.forEach(monthItem => {
      // For monthly summary, we'll create simulated source breakdown
      // In a real scenario, this would come from the API with source/medium breakdown per month
      
      const monthName = new Date(monthItem.month).toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' });
      
      // Simulate distribution across sources based on typical patterns
      const distributions = {
        'direct_none': 0.25,
        'google_organic': 0.20,
        'google_cpc': 0.15,
        'meta_cpc': 0.20,
        'rtb_house': 0.05,
        'mail': 0.05,
        'push': 0.05,
        'other': 0.05
      };
      
      Object.keys(categoryTotals).forEach(groupKey => {
        const distribution = distributions[groupKey] || 0;
        const sessionShare = Math.round(monthItem.sessions * distribution);
        const revenueShare = Math.round(monthItem.revenue * distribution);
        const conversionShare = Math.round(monthItem.eventCount * distribution);
        
        categoryTotals[groupKey].sessions += sessionShare;
        categoryTotals[groupKey].users += Math.round(monthItem.users * distribution);
        categoryTotals[groupKey].conversions += conversionShare;
        categoryTotals[groupKey].revenue += revenueShare;
        
        // Add monthly data point
        categoryTotals[groupKey].monthlyData.push({
          month: monthName,
          sessions: sessionShare,
          conversions: conversionShare,
          revenue: revenueShare
        });
      });
      
      totalSessions += monthItem.sessions;
      totalUsers += monthItem.users;
      totalRevenue += monthItem.revenue;
      totalConversions += monthItem.eventCount;
    });
    
    // Create final sources array for monthly view
    const sources = Object.entries(categoryTotals).map(([key, category]) => {
      const groupOrder = [
        'direct_none',
        'google_organic', 
        'google_cpc',
        'meta_cpc',
        'rtb_house',
        'mail',
        'push',
        'other'
      ];
      
      return {
        key,
        source: category.source,
        medium: '',
        sessions: category.sessions,
        users: category.users,
        new_users: category.new_users,
        conversions: category.conversions,
        revenue: category.revenue,
        conversion_rate: category.sessions > 0 
          ? ((category.conversions / category.sessions) * 100).toFixed(2) 
          : 0,
        trend: 'up', // Default for monthly view
        trend_percentage: '0.0',
        dailyData: category.monthlyData, // Monthly data points
        campaigns: [] // No campaigns in monthly view
      };
    }).sort((a, b) => {
      const groupOrder = [
        'direct_none',
        'google_organic', 
        'google_cpc',
        'meta_cpc',
        'rtb_house',
        'mail',
        'push',
        'other'
      ];
      return groupOrder.indexOf(a.key) - groupOrder.indexOf(b.key);
    });
    
    const avgConversionRate = totalSessions > 0 
      ? (totalConversions / totalSessions * 100).toFixed(1) 
      : 0;
    
    setSourceAnalytics({
      summary: {
        total_sessions: totalSessions,
        total_users: totalUsers,
        conversion_rate: avgConversionRate,
        avg_session_duration: '00:00', // Not available in monthly summary
        bounce_rate: 0, // Not available in monthly summary
        revenue: totalRevenue
      },
      sources
    });
    
    // Set default selected source
    if (!selectedSource && sources.length > 0) {
      setSelectedSource(sources[0]);
      updateChartData(sources[0]);
    } else if (selectedSource) {
      const updatedSource = sources.find(s => s.key === selectedSource.key);
      if (updatedSource) {
        setSelectedSource(updatedSource);
        updateChartData(updatedSource);
      }
    }
    
    setLoading(false);
  };

  // Process traffic source data for the Source/Medium analysis
  const processTrafficSourceData = (data) => {
    if (!data || !data.detailedData || !Array.isArray(data.detailedData) || data.detailedData.length === 0) {
      console.log('No GA traffic source data available or empty data');
      setLoading(false);
      return;
    }
    
    // Filter the data based on the selected date range
    const filteredData = filterDataByDateRange(data.detailedData, dateRange);
    console.log(`Filtered records for ${dateRange}: ${filteredData.length}`);
    
    // Define source/medium groups - these are the only categories we want to show
    const groups = [
      { 
        key: 'direct_none',
        displayName: 'Direct / none',
        match: (source, medium) => 
          source.includes('direct') && medium.includes('none')
      },
      { 
        key: 'google_organic',
        displayName: 'Google / Organic', 
        match: (source, medium) => 
          source.includes('google') && medium.includes('organic')
      },
      { 
        key: 'google_cpc',
        displayName: 'Google CPC', 
        match: (source, medium) => 
          source.includes('google') && medium.includes('cpc')
      },
      { 
        key: 'meta_cpc',
        displayName: 'Meta / CPC', 
        match: (source, medium) => 
          medium.includes('social-cpc') || 
          medium.includes('social_cpc') || 
          medium.includes('social')
      },
      { 
        key: 'rtb_house',
        displayName: 'RTB House', 
        match: (source, medium) => 
          source.includes('rtb')
      },
      { 
        key: 'mail',
        displayName: 'Mail', 
        match: (source, medium) => 
          source.includes('mail') || 
          medium.includes('mail') || 
          medium.includes('email')
      },
      { 
        key: 'push',
        displayName: 'Push', 
        match: (source, medium) => 
          source.includes('push') || 
          medium.includes('push')
      },
      { 
        key: 'other',
        displayName: 'Other', 
        match: () => true // Will always match - for everything else
      }
    ];
    
    // First pass - group items by their category
    const categoryTotals = {};
    
    // Initialize all categories with zero values
    groups.forEach(group => {
      categoryTotals[group.key] = {
        source: group.displayName,
        sessions: 0,
        users: 0,
        new_users: 0,
        conversions: 0,
        revenue: 0,
        dailyData: {}, // Daily data for the source group
        campaigns: {}  // Store aggregated data per campaign within this source group
      };
    });
    
    // For overall page totals
    let totalSessions = 0;
    let totalUsers = 0;
    let totalNewUsers = 0;
    let totalRevenue = 0;
    let totalConversions = 0;
    
    // Önceki dönem verilerini karşılaştırma için saklayacak obje
    const previousPeriodTotals = {};
    
    // Şu anki dönemi ve önceki dönemi hesaplamak için tarih aralığını belirle
    const { startDate: currentStartDate, endDate: currentEndDate } = getDateRangeForFilter(dateRange);
    
    // Önceki dönemin başlangıç ve bitiş tarihlerini hesapla
    const previousStartDate = new Date(currentStartDate);
    const previousEndDate = new Date(currentEndDate);
    const daysDiff = Math.round((currentEndDate - currentStartDate) / (24 * 60 * 60 * 1000));
    
    previousStartDate.setDate(previousStartDate.getDate() - daysDiff);
    previousEndDate.setDate(previousEndDate.getDate() - daysDiff);
    
    // Process each data item
    filteredData.forEach(item => {
      const source = (item.source || 'unknown').toLowerCase();
      const medium = (item.medium || 'unknown').toLowerCase();
      const itemDate = new Date(item.date);
      
      // Find matching group - check each group in order
      // Stop at first match (that's why we put 'other' last)
      let matchedGroup = groups[groups.length - 1]; // Default to 'other'
      
      for (let i = 0; i < groups.length - 1; i++) {
        if (groups[i].match(source, medium)) {
          matchedGroup = groups[i];
          break;
        }
      }
      
      // Update the group totals
      const groupKey = matchedGroup.key;
      categoryTotals[groupKey].sessions += Number(item.sessions) || 0;
      categoryTotals[groupKey].users += Number(item.users) || 0;
      categoryTotals[groupKey].new_users += Number(item.newUsers) || 0;
      categoryTotals[groupKey].conversions += Number(item.eventCount) || 0;
      categoryTotals[groupKey].revenue += Number(item.totalRevenue) || 0;
      
      // Aggregate campaign data within the group
      const campaignName = item.campaignName || '(not set)'; // Handle missing campaign names
      if (!categoryTotals[groupKey].campaigns[campaignName]) {
        categoryTotals[groupKey].campaigns[campaignName] = {
          sessions: 0,
          conversions: 0,
          revenue: 0,
          dailyData: {} // Add daily data tracking for campaigns
        };
      }
      categoryTotals[groupKey].campaigns[campaignName].sessions += Number(item.sessions) || 0;
      categoryTotals[groupKey].campaigns[campaignName].conversions += Number(item.eventCount) || 0; // Assuming eventCount is conversions for now
      categoryTotals[groupKey].campaigns[campaignName].revenue += Number(item.totalRevenue) || 0;
      
      // Günlük verileri grupla
      const dateKey = itemDate.toISOString().split('T')[0];
      if (!categoryTotals[groupKey].dailyData[dateKey]) {
        categoryTotals[groupKey].dailyData[dateKey] = {
          date: dateKey,
          sessions: 0,
          conversions: 0,
          revenue: 0
        };
      }
      
      categoryTotals[groupKey].dailyData[dateKey].sessions += Number(item.sessions) || 0;
      categoryTotals[groupKey].dailyData[dateKey].conversions += Number(item.eventCount) || 0;
      categoryTotals[groupKey].dailyData[dateKey].revenue += Number(item.totalRevenue) || 0;
      
      // Aggregate daily campaign data
      if (!categoryTotals[groupKey].campaigns[campaignName].dailyData[dateKey]) {
        categoryTotals[groupKey].campaigns[campaignName].dailyData[dateKey] = {
          date: dateKey,
          sessions: 0,
          conversions: 0,
          revenue: 0
        };
      }
      categoryTotals[groupKey].campaigns[campaignName].dailyData[dateKey].sessions += Number(item.sessions) || 0;
      categoryTotals[groupKey].campaigns[campaignName].dailyData[dateKey].conversions += Number(item.eventCount) || 0;
      categoryTotals[groupKey].campaigns[campaignName].dailyData[dateKey].revenue += Number(item.totalRevenue) || 0;
      
      // Add to page totals
      totalSessions += Number(item.sessions) || 0;
      totalUsers += Number(item.users) || 0;
      totalNewUsers += Number(item.newUsers) || 0;
      totalRevenue += Number(item.totalRevenue) || 0;
      totalConversions += Number(item.eventCount) || 0;
    });
    
    // Şimdi önceki dönem verilerini hesaplayalım
    data.detailedData.forEach(item => {
      if (!item.date) return;
      
      const itemDate = new Date(item.date);
      // Önceki dönem verilerini filtrele
      if (itemDate >= previousStartDate && itemDate <= previousEndDate) {
        const source = (item.source || 'unknown').toLowerCase();
        const medium = (item.medium || 'unknown').toLowerCase();
        
        // Find matching group
        let matchedGroup = groups[groups.length - 1]; // Default to 'other'
        
        for (let i = 0; i < groups.length - 1; i++) {
          if (groups[i].match(source, medium)) {
            matchedGroup = groups[i];
            break;
          }
        }
        
        // Önceki dönem verilerini kaydet
        if (!previousPeriodTotals[matchedGroup.key]) {
          previousPeriodTotals[matchedGroup.key] = { sessions: 0 };
        }
        
        previousPeriodTotals[matchedGroup.key].sessions += Number(item.sessions) || 0;
      }
    });
    
    // Log what we found for debug purposes
    console.log('Category totals after processing:', Object.entries(categoryTotals).map(([key, data]) => ({
      key,
      sessions: data.sessions
    })));
    
    // Calculate derived metrics for each category and create final array
    const sources = Object.entries(categoryTotals).map(([key, category]) => {
      // Günlük verileri diziye dönüştür
      const dailyData = Object.values(category.dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      // Önceki dönem oturum sayısını al, yoksa 0 kullan
      const previousSessions = previousPeriodTotals[key]?.sessions || 0;
      
      // Convert campaigns object to sorted array
      const campaignsArray = Object.entries(category.campaigns)
        .map(([name, metrics]) => ({
          name: name,
          sessions: metrics.sessions,
          conversions: metrics.conversions,
          revenue: metrics.revenue,
          dailyData: Object.values(metrics.dailyData).sort((a, b) => new Date(a.date) - new Date(b.date)), // Convert campaign daily data to sorted array
          conversion_rate: metrics.sessions > 0 
            ? ((metrics.conversions / metrics.sessions) * 100).toFixed(2) 
            : '0.00',
        }))
        .sort((a, b) => b.sessions - a.sessions); // Sort campaigns by sessions descending
      
      // Gerçek trend hesaplama
      let trendValue = 0;
      let trendDirection = 'up';
      
      if (previousSessions > 0) {
        trendValue = ((category.sessions - previousSessions) / previousSessions * 100).toFixed(1);
        trendDirection = parseFloat(trendValue) >= 0 ? 'up' : 'down';
      }
      
      return {
        key,
        source: category.source,
        medium: '',  // No medium needed for group totals
        sessions: category.sessions,
        users: category.users,
        new_users: category.new_users,
        conversions: category.conversions,
        revenue: category.revenue,
        conversion_rate: category.sessions > 0 
          ? ((category.conversions / category.sessions) * 100).toFixed(2) 
          : 0,
        trend: trendDirection,
        trend_percentage: trendValue, // Trend percentage for the source group
        dailyData: dailyData,         // Daily aggregated data for the source group
        campaigns: campaignsArray     // Array of campaigns within this group with their metrics
      };
    });
    
    // Define the ordering of groups
    const groupOrder = [
      'direct_none',
      'google_organic', 
      'google_cpc',
      'meta_cpc',
      'rtb_house',
      'mail',
      'push',
      'other'
    ];
    
    // Sort by custom group order
    sources.sort((a, b) => {
      return groupOrder.indexOf(a.key) - groupOrder.indexOf(b.key);
    });
    
    // Calculate average conversion rate for the page
    const avgConversionRate = totalSessions > 0 
      ? (totalConversions / totalSessions * 100).toFixed(1) 
      : 0;
    
    // Update the state with processed data
    setSourceAnalytics({
      summary: {
        total_sessions: totalSessions,
        total_users: totalUsers,
        conversion_rate: avgConversionRate,
        avg_session_duration: formatDuration(data.totals.avgSessionDuration || 0),
        bounce_rate: 100 - (data.totals.engagedSessions / data.totals.sessions * 100 || 0).toFixed(1),
        revenue: totalRevenue
      },
      sources
    });
    
    // Set default selected source if not already set
    if (!selectedSource && sources.length > 0) {
      setSelectedSource(sources[0]);
      updateChartData(sources[0]);
    } else if (selectedSource) {
      // Eğer önceden seçilmiş bir kaynak varsa, yeni verilerle güncelleyelim
      const updatedSource = sources.find(s => s.key === selectedSource.key);
      if (updatedSource) {
        setSelectedSource(updatedSource);
        updateChartData(updatedSource);
      }
    } else {
      // Just update with filtered data if no source is selected
      updateChartData(null, null, filteredData);
    }
    
    // Reset expanded state when data reloads
    setExpandedSources({});
    // Done loading
    setLoading(false);
  };

  // Mock veri
  const data = {
    summary: {
      total_sessions: 124750,
      total_users: 89420,
      conversion_rate: 3.2,
      avg_session_duration: '2:45',
      bounce_rate: 42.8,
      revenue: 287450
    },
    sources: [
      {
        source: 'Google',
        medium: 'organic',
        sessions: 45280,
        users: 32450,
        new_users: 28740,
        conversions: 1240,
        revenue: 98450,
        conversion_rate: 2.74,
        trend: 'up',
        trend_percentage: 12.4
      },
      {
        source: 'Facebook',
        medium: 'paid',
        sessions: 28450,
        users: 22180,
        new_users: 18920,
        conversions: 890,
        revenue: 72340,
        conversion_rate: 3.12,
        trend: 'up',
        trend_percentage: 8.7
      },
      {
        source: 'Instagram',
        medium: 'paid',
        sessions: 22750,
        users: 18640,
        new_users: 15280,
        conversions: 720,
        revenue: 58920,
        conversion_rate: 3.16,
        trend: 'up',
        trend_percentage: 15.2
      },
      {
        source: 'Direct',
        medium: 'none',
        sessions: 18920,
        users: 15240,
        new_users: 8450,
        conversions: 520,
        revenue: 42680,
        conversion_rate: 2.75,
        trend: 'down',
        trend_percentage: 2.1
      },
      {
        source: 'Bing',
        medium: 'organic',
        sessions: 12450,
        users: 9840,
        new_users: 7250,
        conversions: 280,
        revenue: 22450,
        conversion_rate: 2.25,
        trend: 'up',
        trend_percentage: 5.8
      },
      {
        source: 'YouTube',
        medium: 'referral',
        sessions: 8920,
        users: 7240,
        new_users: 5840,
        conversions: 180,
        revenue: 15280,
        conversion_rate: 2.02,
        trend: 'up',
        trend_percentage: 18.4
      },
      {
        source: 'Twitter',
        medium: 'paid',
        sessions: 7840,
        users: 6280,
        new_users: 4920,
        conversions: 150,
        revenue: 12840,
        conversion_rate: 1.91,
        trend: 'down',
        trend_percentage: 1.2
      },
      {
        source: 'LinkedIn',
        medium: 'paid',
        sessions: 6920,
        users: 5480,
        new_users: 4180,
        conversions: 210,
        revenue: 18240,
        conversion_rate: 3.03,
        trend: 'up',
        trend_percentage: 22.5
      },
      {
        source: 'Pinterest',
        medium: 'referral',
        sessions: 4580,
        users: 3840,
        new_users: 2980,
        conversions: 85,
        revenue: 7240,
        conversion_rate: 1.86,
        trend: 'up',
        trend_percentage: 4.2
      },
      {
        source: 'Email',
        medium: 'email',
        sessions: 15280,
        users: 12450,
        new_users: 8920,
        conversions: 480,
        revenue: 38920,
        conversion_rate: 3.14,
        trend: 'up',
        trend_percentage: 9.8
      },
      {
        source: 'TikTok',
        medium: 'paid',
        sessions: 12840,
        users: 10280,
        new_users: 8840,
        conversions: 320,
        revenue: 25840,
        conversion_rate: 2.49,
        trend: 'up',
        trend_percentage: 28.4
      },
      {
        source: 'Reddit',
        medium: 'referral',
        sessions: 3840,
        users: 3120,
        new_users: 2580,
        conversions: 65,
        revenue: 5280,
        conversion_rate: 1.69,
        trend: 'down',
        trend_percentage: 0.8
      }
    ]
  };

  // Mock veri - Trend analizi için veri eklendi
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Sessions',
        data: [],
        borderColor: 'rgb(59, 130, 246)', // Blue for sessions
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y', // Assign to the first y-axis
      },
      {
        label: 'Revenue',
        data: [],
        borderColor: 'rgb(16, 185, 129)', // Green for revenue
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1', // Assign to the second y-axis
      }
    ]
  });

  // Update chart data based on source data - Now always includes Sessions and Revenue
  const updateChartData = (source, campaign, filteredData = null) => {
    let dateData = [];
    let chartTitlePrefix = 'Overall'; // Default title prefix

    if (campaign && source) { // Check if campaign AND source are selected
      // Use campaign's daily data
      dateData = campaign.dailyData || [];
      chartTitlePrefix = `${source.source} > ${campaign.name}`; // e.g., "Google CPC > Campaign A"
    } else if (source) {
      // Use source's daily data
      dateData = source.dailyData || [];
      chartTitlePrefix = source.source; // e.g., "Google CPC"
    } else if (filteredData) {
      // Use aggregated daily data from all filtered items
      const dateMap = {};
      
      filteredData.forEach(item => {
        if (!item.date) return;
        
        const date = new Date(item.date);
        const dateKey = date.toISOString().split('T')[0];
        
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            sessions: 0,
            conversions: 0,
            revenue: 0
          };
        }
        
        dateMap[dateKey].sessions += Number(item.sessions) || 0;
        dateMap[dateKey].conversions += Number(item.eventCount) || 0;
        dateMap[dateKey].revenue += Number(item.totalRevenue) || 0;
      });
      
      // Convert to array and sort by date
      dateData = Object.values(dateMap)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      chartTitlePrefix = 'All Sources';
    }

    if (dateData.length === 0) {
       // Clear chart if no data for the selection
       setChartData({
         labels: [],
         datasets: [
           { ...chartData.datasets[0], data: [] },
           { ...chartData.datasets[1], data: [] } 
         ]
       });
       return;
    }
    
    // Format date labels based on view mode
    const labels = dateData.map(d => {
      if (viewMode === 'monthly') {
        // For monthly view, use month names directly if available, otherwise parse date
        return d.month || new Date(d.date).toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' });
      } else {
        // For daily view, show day and month
        const date = new Date(d.date);
        return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`;
      }
    });
    
    // Create chart data sets
    const sessions = dateData.map(d => d.sessions);
    const conversions = dateData.map(d => d.conversions); // Keep conversion data for potential future use
    const revenue = dateData.map(d => d.revenue);
    
    // Update chart based on currently selected metric - REMOVED, now always show Sessions and Revenue
    let newData = {
          labels,
      datasets: [
        {
          label: `${chartTitlePrefix} - Sessions`,
            data: sessions,
          borderColor: 'rgb(59, 130, 246)', // Blue for sessions
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
          tension: 0.4,
          yAxisID: 'y', // Assign to the first y-axis
        },
        {
          label: `${chartTitlePrefix} - Revenue`,
          data: revenue,
          borderColor: 'rgb(16, 185, 129)', // Green for revenue
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1', // Assign to the second y-axis
        }
      ]
        };
    
    setChartData(newData);
  };

  // Şimdi kaynak tıklama işleyicisini ekleyelim
  const handleSourceClick = (source) => {
    setSelectedSource(source);
    setSelectedCampaign(null); // Clear selected campaign when a source is clicked
    // updateChartData will be called by the useEffect dependency change
  };

  // Add handler for campaign click
  const handleCampaignClick = (campaign, parentSource) => {
    console.log("[handleCampaignClick] Campaign clicked:", campaign?.name, "Parent:", parentSource?.source);
    setSelectedCampaign(campaign);
    setSelectedSource(parentSource);
  };

  // Toggle campaign list visibility
  const toggleSourceExpansion = (sourceKey) => {
    setExpandedSources(prev => ({
      ...prev,
      [sourceKey]: !prev[sourceKey]
    }));
  };

  useEffect(() => {
    // Fetch data from the API when dateRange or viewMode changes
    fetchGATrafficSourceData();
  }, [dateRange, viewMode]);

  // << ADD useEffect to update chart when selection changes >>
  useEffect(() => {
    console.log(`[useEffect for Chart Update] Triggered. Source: ${selectedSource?.source}, Campaign: ${selectedCampaign?.name}`);
    if (selectedCampaign && selectedSource) {
      // Campaign is selected, show campaign data
      updateChartData(selectedSource, selectedCampaign);
    } else if (selectedSource) {
      // Only source is selected, show source data
      updateChartData(selectedSource, null);
    } else {
       // Update chart with overall data when no source is selected
       // We need the raw filtered data here. Pass null for source, and get the filtered data.
       // Re-filtering might be inefficient, consider storing filtered data if needed often.
       const filtered = filterDataByDateRange(trafficSourceData?.detailedData || [], dateRange);
       updateChartData(null, null, filtered); 
    }
   // Depend on source selection (and potentially campaign if chart needs to react)
   // Removed selectedMetric dependency
  }, [selectedSource, selectedCampaign, trafficSourceData, dateRange]); // Add selectedCampaign dependency

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="loading-logo-container">
          <img 
            src="/logoicon.png" 
            alt="Glowytics Logo" 
            className="loading-logo"
          />
        </div>
      </div>
    );
  }

  const ExportMenu = () => (
    <div className="relative">
      <button 
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
        onClick={() => setExportMenuOpen(!exportMenuOpen)}
      >
        <Download size={16} />
        <span>Export</span>
        <ChevronDown size={16} />
      </button>
      
      {exportMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle PDF export
                setExportMenuOpen(false);
              }}
            >
              Export as PDF
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle Excel export
                setExportMenuOpen(false);
              }}
            >
              Export as Excel
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                // Handle CSV export
                setExportMenuOpen(false);
              }}
            >
              Export as CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header - updated to match GA4Overview */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <img 
              src="/Google-Analytics-Logo.png" 
              alt="Analytics Icon" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Source/Medium Analysis {viewMode === 'monthly' ? '- Monthly View' : '- Daily View'}
              </h1>
              <p className="text-gray-500 mt-1">
                {viewMode === 'monthly' 
                  ? 'Monthly aggregated performance metrics of your traffic sources'
                  : 'Performance metrics of your traffic sources'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <select 
                className="pl-10 pr-4 py-2 bg-white border rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 days</option>
                <option value="last30">Last 30 days</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="last90">Last 90 days</option>
                <option value="all">All Data</option>
              </select>
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('daily')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'daily' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Daily
              </button>
              <button
                onClick={() => setViewMode('monthly')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'monthly' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Monthly
              </button>
            </div>
            
            <button 
              className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
              onClick={fetchGATrafficSourceData}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
              <Download className="w-4 h-4" />
              <span>Export Report</span>
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4">
          <Card className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-xl border border-blue-400">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-blue-100 text-xs">Total Sessions</p>
                <p className="text-xl font-semibold mt-1">{sourceAnalytics.summary.total_sessions.toLocaleString()}</p>
                <div className="flex items-center mt-2 text-blue-100 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>8.3%</span>
                </div>
              </div>
              <div className="p-2 bg-white/10 rounded-lg">
                <Users className="w-4 h-4" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Users</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">{sourceAnalytics.summary.total_users.toLocaleString()}</p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>12.4%</span>
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <Globe className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Total Conversions</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  {(sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100).toFixed(0)}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>5.2%</span>
                </div>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg">
                <ShoppingBag className="w-4 h-4 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Conversion Rate</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">{sourceAnalytics.summary.conversion_rate}%</p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>2.1%</span>
                </div>
              </div>
              <div className="p-2 bg-green-50 rounded-lg">
                <Zap className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Revenue per Conversion</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">
                  ₺{sourceAnalytics.summary.revenue / (sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100) > 0 
                    ? (sourceAnalytics.summary.revenue / (sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100)).toFixed(2) 
                    : '0.00'}
                </p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>3.8%</span>
                </div>
              </div>
              <div className="p-2 bg-orange-50 rounded-lg">
                <TrendingUp className="w-4 h-4 text-orange-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-xl shadow-xl border border-gray-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-xs">Total Revenue</p>
                <p className="text-xl font-semibold mt-1 text-gray-900">₺{sourceAnalytics.summary.revenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</p>
                <div className="flex items-center mt-2 text-green-600 text-xs">
                  <ArrowUp className="w-3 h-3 mr-1" />
                  <span>15.7%</span>
                </div>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-6">
          <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
            <div className="p-6 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="font-semibold text-lg text-gray-800">
                Source Performance 
                <span className="ml-2 text-sm text-gray-500 font-normal">
                  {dateRange === 'today' && '(Today)'}
                  {dateRange === 'yesterday' && '(Yesterday)'}
                  {dateRange === 'last7' && '(Last 7 Days)'}
                  {dateRange === 'last30' && '(Last 30 Days)'}
                  {dateRange === 'thisMonth' && '(This Month)'}
                  {dateRange === 'lastMonth' && '(Last Month)'}
                  {dateRange === 'last90' && '(Last 90 Days)'}
                  {dateRange === 'all' && '(All Data)'}
                </span>
              </h2>
              
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <div className="relative w-full sm:w-auto">
                  <input
                    type="text"
                    placeholder="Search sources..."
                    className="pl-9 pr-4 py-2 bg-gray-50 border rounded-lg text-sm w-full sm:w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                
                <button 
                  className="p-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <SlidersHorizontal className="w-5 h-5" />
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="p-6 bg-gray-50 border-b">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Minimum Sessions</label>
                    <input type="number" className="w-full px-3 py-1.5 border rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Minimum Conversions</label>
                    <input type="number" className="w-full px-3 py-1.5 border rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Source Type</label>
                    <select className="w-full px-3 py-1.5 border rounded-lg text-sm">
                      <option>All</option>
                      <option>Organic</option>
                      <option>Paid</option>
                      <option>Social</option>
                      <option>Referral</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="pl-4 pr-4 py-3 text-xs font-medium text-gray-500 w-[calc(theme(space.8)+theme(space.4)+250px)]">SOURCE / CAMPAIGN</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500">SESSIONS</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500">CONVERSIONS</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500">CONV. RATE</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-500">REVENUE</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sourceAnalytics.sources
                    .filter(source => 
                      source.source.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((source, index) => (
                    <React.Fragment key={source.key}>
                      <tr 
                        className={`hover:bg-gray-50 transition-colors ${selectedSource && selectedSource.key === source.key ? 'bg-blue-50' : ''}`}
                      >
                        <td className="pl-4 pr-4 py-4 whitespace-nowrap" onClick={() => handleSourceClick(source)}>
                          <div className="flex items-center gap-2">
                            {viewMode === 'daily' && source.campaigns && source.campaigns.length > 0 && (
                              <button 
                                onClick={(e) => { e.stopPropagation(); toggleSourceExpansion(source.key); }}
                                className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                              >
                                {expandedSources[source.key] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </button>
                            )}
                            {(viewMode === 'monthly' || !(source.campaigns && source.campaigns.length > 0)) && (
                              <div className="w-4 h-4 flex-shrink-0"></div>
                            )}
                            <span className="font-medium text-sm text-gray-900 cursor-pointer truncate">{source.source}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-sm text-gray-900">{source.sessions.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-sm text-gray-900">{source.conversions.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className={`font-medium text-sm ${Number(source.conversion_rate) >= 3 ? 'text-green-600' : 'text-gray-900'}`}>
                            {source.conversion_rate}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-sm text-gray-900">₺{source.revenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</div>
                        </td>
                      </tr>

                      {/* Conditionally Rendered Campaign Rows - Only in daily view */}
                       {viewMode === 'daily' && expandedSources[source.key] && source.campaigns && source.campaigns.map((campaign, campaignIndex) => (
                         <tr
                           key={`${source.key}-campaign-${campaignIndex}`}
                           className={`bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer ${selectedCampaign && selectedCampaign.name === campaign.name && selectedSource.key === source.key ? '!bg-blue-100' : ''}`}
                           onClick={() => handleCampaignClick(campaign, source)}
                         >
                           <td className="pl-12 pr-4 py-3 whitespace-nowrap text-sm text-gray-600">
                             {campaign.name}
                           </td>
                           <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">{campaign.sessions.toLocaleString()}</td>
                           <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">{campaign.conversions.toLocaleString()}</td>
                           <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">{campaign.conversion_rate}%</td>
                           <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">₺{campaign.revenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
                         </tr>
                       ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-gray-50 z-10">
                  <tr className="bg-gray-50 border-t-2 border-gray-200">
                    <td className="pl-4 pr-4 py-4 whitespace-nowrap">
                      <div className="font-semibold text-sm text-gray-900">TOTAL</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-sm text-gray-900">
                        {sourceAnalytics.summary.total_sessions.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-sm text-gray-900">
                        {Math.round(sourceAnalytics.summary.total_sessions * sourceAnalytics.summary.conversion_rate / 100).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-sm text-gray-900">
                        {sourceAnalytics.summary.conversion_rate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-sm text-gray-900">
                        ₺{sourceAnalytics.summary.revenue.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        <div className="col-span-6 space-y-6">
          <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">
                  {selectedCampaign && selectedSource 
                    ? `${selectedSource.source} > ${selectedCampaign.name} Trend Analysis` 
                    : selectedSource 
                      ? `${selectedSource.source} Trend Analysis` 
                      : 'Overall Trend Analysis'}
                </h3>
              </div>
              <div className="h-80">
                <LineChart 
                  data={chartData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: function(tooltipItem) {
                            const datasetLabel = tooltipItem.dataset.label || '';
                            let value = tooltipItem.formattedValue;
                            if (tooltipItem.dataset.yAxisID === 'y1') {
                              value = '₺' + Number(tooltipItem.raw).toLocaleString('tr-TR');
                            }
                            return `${datasetLabel}: ${value}`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: {
                          display: true,
                          text: 'Sessions',
                          color: 'rgb(59, 130, 246)' // Blue
                        },
                        ticks: {
                          color: 'rgb(59, 130, 246)'
                        }
                      },
                      y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                          display: true,
                          text: 'Revenue (₺)',
                          color: 'rgb(16, 185, 129)' // Green
                        },
                        ticks: {
                           color: 'rgb(16, 185, 129)',
                           // Format revenue ticks as currency
                           callback: function(value) {
                             return '₺' + value.toLocaleString('tr-TR');
                           }
                        },
                        // Ensure grid lines don't overlap from both axes
                        grid: {
                          drawOnChartArea: false, 
                        },
                      }
                    }
                  }} 
                />
              </div>
              {selectedSource && (
                <div className="mt-3 text-center text-sm text-gray-500">
                  Selected source: <span className="font-medium text-blue-600">{selectedSource.source}</span>
                </div>
              )}
              {selectedCampaign && (
                <div className="mt-1 text-center text-sm text-gray-500">
                  Selected campaign: <span className="font-medium text-blue-600">{selectedCampaign.name}</span>
                </div>
              )}
            </div>
          </Card>
           
          {selectedSource && (
            <Card className="border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <h3 className="font-semibold text-gray-800 mb-4">
                  {selectedCampaign
                    ? `Campaign Summary: ${selectedSource.source} > ${selectedCampaign.name}`
                    : `Source Summary: ${selectedSource.source}`}
                </h3>
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-800">
                          Total Sessions: <span className="font-medium">{(selectedCampaign ? selectedCampaign.sessions : selectedSource.sessions).toLocaleString()}</span>
                          {!selectedCampaign && Number(selectedSource.trend_percentage) !== 0 && (
                            <span className={`ml-1 ${selectedSource.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                              ({selectedSource.trend === 'up' ? '+' : ''}{selectedSource.trend_percentage}%)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Target className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-green-800">
                          Conversion Rate: <span className="font-medium">{(selectedCampaign ? selectedCampaign.conversion_rate : selectedSource.conversion_rate)}%</span> 
                          ({(selectedCampaign ? selectedCampaign.conversions : selectedSource.conversions).toLocaleString()} conversions)
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <DollarSign className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-purple-800">
                          Total Revenue: <span className="font-medium">₺{(selectedCampaign ? selectedCampaign.revenue : selectedSource.revenue).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
} 